import { type V2, angleDiff, angleTo, normalize, sub } from '../core/math';
import type { Actor } from './actor';
import { forEachPassive } from './passives';
import { registry } from './registry';
import { levelMult } from './skills';
import type { HitSpec, ResolvedSkill, StatusApply } from './skillTypes';
import { defenseMultiplier } from './stats';
import { getStatus } from './status';
import {
  type Element,
  GRADE_RANK,
  type Reaction,
  STAGGER_POINTS,
  SUPER_ARMOR_RANK,
  type SuperArmor,
  isBossGrade,
} from './types';

/** Positional arcs: back = 90 degrees behind the target, head = 90 degrees in front. */
export const BACK_ARC = Math.PI / 4;
export const HEAD_ARC = Math.PI / 4;
export const BACK_DMG = 0.05;
export const BACK_CRIT = 0.1;
export const HEAD_DMG = 0.2;
export const HEAD_STAGGER = 0.1;
export const STAGGERED_DURATION = 6;
export const COUNTERED_DURATION = 3;

export interface HitContext {
  skill: ResolvedSkill | null;
  hit: HitSpec;
  /** Multiplier from charge level / perfect zone / multi-hit split. */
  mult: number;
  /** Where the hit comes from (for positional checks and knockback direction). */
  from: V2;
  isDot?: boolean;
}

/** Everything needed to apply a hit on the target's authoritative peer. */
export interface HitResult {
  srcId: number;
  tgtId: number;
  amount: number;
  crit: boolean;
  back: boolean;
  head: boolean;
  counter: boolean;
  stagger: number;
  weakPoint: number;
  element: Element;
  statuses: StatusApply[];
  reaction: Reaction | null;
  from: V2;
  /** Source attack power for DoTs applied by this hit. */
  srcAttack: number;
  heal: number;
  shield: number;
  shieldDuration: number;
  friendly: boolean;
  skillId: string | null;
  isDot: boolean;
  /** Status ids to remove from the target (friendly results only). */
  cleanse?: string[];
}

export type Positional = { back: boolean; head: boolean };

/** Which side of the target the attacker is on. */
export function positionalOf(target: Actor, from: V2): Positional {
  const rel = Math.abs(angleDiff(target.facing, angleTo(target.pos, from)));
  return { back: rel >= Math.PI - BACK_ARC, head: rel <= HEAD_ARC };
}

export function isDisabled(a: Actor): boolean {
  return a.statuses.some((s) => getStatus(s.id).cc?.disable) || (a.monster?.countered ?? 0) > 0;
}

export function superArmorOf(a: Actor): SuperArmor {
  let sa: SuperArmor = a.superArmorBonus;
  if (a.cast && SUPER_ARMOR_RANK[a.cast.skill.superArmor] > SUPER_ARMOR_RANK[sa]) sa = a.cast.skill.superArmor;
  if (a.hasStatus('statusImmune')) sa = 'full';
  return sa;
}

function elementMult(target: Actor, el: Element): number {
  const w = target.monster?.weakness;
  return w && el === w ? 1.15 : 1;
}

/**
 * Computes a hit against a target from the attacker's point of view. Pure with
 * respect to the target (nothing is mutated), so it can run on the attacker's
 * peer and be shipped to the target's owner as a claim.
 */
export function computeHit(src: Actor, target: Actor, ctx: HitContext, rand: () => number): HitResult {
  const { skill, hit } = ctx;
  const friendly = !!hit.friendly;
  const element: Element = hit.element ?? skill?.element ?? 'none';
  const base: HitResult = {
    srcId: src.id,
    tgtId: target.id,
    amount: 0,
    crit: false,
    back: false,
    head: false,
    counter: false,
    stagger: 0,
    weakPoint: 0,
    element,
    statuses: [],
    reaction: null,
    from: { ...ctx.from },
    srcAttack: src.stats.attack,
    heal: 0,
    shield: 0,
    shieldDuration: hit.shieldDuration ?? 0,
    friendly,
    skillId: skill?.id ?? null,
    isDot: !!ctx.isDot,
  };

  if (friendly) {
    base.heal = (hit.healPct ?? 0) * src.stats.maxHp * ctx.mult;
    base.shield = (hit.shieldPct ?? 0) * src.stats.maxHp * ctx.mult;
    base.statuses = [...(hit.status ?? [])];
    return base;
  }

  const positionalTag = skill?.positional ?? 'none';
  const pos = positionalOf(target, src.pos);
  const back = pos.back && (positionalTag === 'back' || positionalTag === 'both');
  const head = pos.head && (positionalTag === 'head' || positionalTag === 'both');

  const tmods = target.stats.mods;
  let passiveCrit = 0;
  let passiveCritDmg = 0;
  forEachPassive(src, (h) => {
    passiveCrit += h.critRate?.(src, target, skill) ?? 0;
    passiveCritDmg += h.critDmg?.(src, skill) ?? 0;
  });
  const critChance =
    src.stats.critRate + (skill?.critRateAdd ?? 0) + (back ? BACK_CRIT : 0) + tmods.critTakenAdd + passiveCrit;
  const crit = !ctx.isDot && rand() < critChance;

  const levelK = skill ? levelMult(skill.level) : 1;
  let dmg = src.stats.attack * hit.coef * ctx.mult * levelK * (skill?.dmgMult ?? 1);
  dmg *= 1 + src.stats.dmgBonus;
  if (skill && (skill.kind === 'awakening' || skill.kind === 'identity')) dmg *= 1 + src.stats.specBonus;
  if (skill && src.player) {
    const cls = registry.classes.get(src.player.classId);
    dmg *= cls?.identity.damageMult?.(src, skill, target) ?? 1;
  }
  if (crit) dmg *= src.stats.critDmg + (skill?.critDmgAdd ?? 0) + passiveCritDmg;
  forEachPassive(src, (h) => {
    if (h.outDamage) dmg *= h.outDamage({ src, target, skill, back, head, crit });
  });
  forEachPassive(target, (h) => {
    if (h.inDamage) dmg *= h.inDamage(target, src);
  });
  if (back) dmg *= 1 + BACK_DMG + src.stats.backBonus;
  if (head) dmg *= 1 + HEAD_DMG + src.stats.headBonus;
  dmg *= elementMult(target, element);
  dmg *= defenseMultiplier(target.stats.defense, skill?.defPen ?? 0);
  dmg *= 1 + tmods.dmgTakenPct;
  dmg *= 1 - target.stats.dmgReduction;
  if (target.monster) {
    const g = GRADE_RANK[target.monster.grade];
    if (g <= GRADE_RANK.elite) dmg *= skill?.vsWeakMult ?? 1;
    if (target.monster.immunePush) dmg *= skill?.vsBossMult ?? 1;
  }
  if (isDisabled(target)) dmg *= 1 + src.stats.vsCCBonus;
  if (!ctx.isDot) dmg *= 0.95 + rand() * 0.1;

  // Stagger does not scale with attack power, only with tier, head attacks and the stagger stat.
  // ctx.mult already carries the multi-hit split and charge multipliers.
  const tier = hit.stagger ?? skill?.stagger ?? 'none';
  let stagger = STAGGER_POINTS[tier] * src.stats.staggerMult * ctx.mult;
  forEachPassive(src, (h) => {
    if (h.staggerMult) stagger *= h.staggerMult();
  });
  if (head) stagger *= 1 + HEAD_STAGGER;
  stagger *= 1 + tmods.staggerTakenPct;

  let weakPoint = hit.weakPoint ?? skill?.weakPoint ?? 0;
  if (weakPoint > 0 && target.hasStatus('corroded')) weakPoint += 1;

  const counter =
    !!skill?.counter &&
    !!target.monster &&
    target.monster.counterUntil > 0 &&
    // Counters must hit the front half of the target.
    Math.abs(angleDiff(target.facing, angleTo(target.pos, src.pos))) <= Math.PI / 2;

  const statuses: StatusApply[] = [...(hit.status ?? []), ...(skill?.extraStatus ?? [])];
  if (skill?.synergy) statuses.push(skill.synergy);

  return {
    ...base,
    amount: Math.max(1, Math.round(dmg)),
    crit,
    back,
    head,
    counter,
    stagger,
    weakPoint,
    statuses: statuses.filter((st) => st.chance === undefined || rand() < st.chance),
    reaction: hit.reaction ?? null,
  };
}

/** Whether a reaction/CC gets through the target's defences. */
export function reactionAllowed(target: Actor, reaction: Reaction): boolean {
  if (reaction.kind === 'none') return false;
  const sa = SUPER_ARMOR_RANK[superArmorOf(target)];
  if (target.monster && (target.monster.immunePush || isBossGrade(target.monster.grade))) return false;
  if (reaction.kind === 'flinch') return sa < SUPER_ARMOR_RANK.paralysis;
  return sa < SUPER_ARMOR_RANK.push;
}

export function hardCCAllowed(target: Actor): boolean {
  if (target.monster && (target.monster.immuneCC || isBossGrade(target.monster.grade))) return false;
  if (target.hasStatus('debuffImmune')) return false;
  return SUPER_ARMOR_RANK[superArmorOf(target)] < SUPER_ARMOR_RANK.full;
}

/** Direction to push a target for a reaction. */
export function pushDir(target: Actor, from: V2): V2 {
  const d = normalize(sub(target.pos, from));
  return d.x === 0 && d.z === 0 ? { x: Math.sin(target.facing + Math.PI), z: Math.cos(target.facing + Math.PI) } : d;
}
